import moment from 'moment-timezone';
import { differenceInMinutes } from 'date-fns';
import React, { useState } from 'react';
import cx from 'classnames';

import Button, {
  ButtonColorUnion,
  ButtonSizeUnion,
  ButtonLevel,
} from 'components/ButtonDeprecated';
import Divider from 'components/Divider';
import Link from 'components/Link';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import TextBadge from 'components/TextBadge';
import TooltipTrigger from 'components/TooltipTrigger';
import Tooltip from 'components/Tooltip';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import {
  CoachingSessionInstance,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import 'coaching/components/styles/CoachingSessionUpcomingInstanceRows.scss';

export type Props = {
  instances: CoachingSessionInstance[];
};

const DEFAULT_ROWS_TO_SHOW = 4;
const MAX_MINUTES_BEFORE_SESSION_TO_ALLOW_USER_TO_JOIN = 30;

export default function CoachingSessionUpcomingInstanceRows({
  instances,
  sessionTools,
  recurringScheduleUuids,
}: {
  instances: CoachingSessionInstance[];
  sessionTools?: CoachingSessionTools;
  recurringScheduleUuids: string[];
}) {
  const [showAllInstances, setShowAllInstances] = useState(false);
  const instancesToShow = showAllInstances ? instances : instances.slice(0, DEFAULT_ROWS_TO_SHOW);
  const shouldShowExpansionAction = instances.length >= DEFAULT_ROWS_TO_SHOW;
  const [selectedInstanceUuid, setSelectedInstanceUuid] = useState(instancesToShow[0].uuid);

  return (
    <>
      {instancesToShow.map((instance, index) => {
        const isLastItem = index === instances.length - 1;
        const shouldShowDivider = shouldShowExpansionAction || !isLastItem;

        return (
          <React.Fragment key={instance.uuid}>
            <CoachingSessionUpcomingInstanceRow
              instance={instance}
              isFirstItem={index === 0}
              isSelected={instance.uuid === selectedInstanceUuid}
              onSelectInstance={() => setSelectedInstanceUuid(instance.uuid)}
              sessionTools={sessionTools}
              instanceIsPartOfRecurringSchedule={recurringScheduleUuids.some(
                uuid => uuid === instance.coachingSessionScheduleUuid
              )}
              userHasNoRecurringSchedules={recurringScheduleUuids.length === 0}
            />
            {shouldShowDivider && (
              <Divider className="CoachingSessionUpcomingInstanceRows-divider" size="flush" />
            )}
          </React.Fragment>
        );
      })}
      {shouldShowExpansionAction && (
        <Link
          className="CoachingSessionUpcomingInstanceRows-expansionAction"
          color="chalkboard"
          onClick={() => setShowAllInstances(!showAllInstances)}>
          <Text weight="semibold" size="medium">
            View {showAllInstances ? 'fewer' : 'more'} sessions
          </Text>
        </Link>
      )}
    </>
  );
}

function CoachingSessionUpcomingInstanceRow({
  instance,
  isSelected,
  isFirstItem,
  sessionTools,
  instanceIsPartOfRecurringSchedule,
  userHasNoRecurringSchedules,
  onSelectInstance,
}: {
  instance: CoachingSessionInstance;
  isSelected: boolean;
  isFirstItem: boolean;
  sessionTools?: CoachingSessionTools;
  instanceIsPartOfRecurringSchedule: boolean;
  userHasNoRecurringSchedules: boolean;
  onSelectInstance: () => void;
}) {
  if (isFirstItem && instance.isCancelled) {
    // if this is the next upcoming instance but it's cancelled,
    // there's no need to show it
    return null;
  }

  const dateTimeString = constructDateTimeString(instance.startDateTime);
  const coachString = `Coach ${instance.coachName.split(' ')[0]}`;
  const oneOffCoachString = instanceIsPartOfRecurringSchedule ? '' : `with ${coachString} `;
  const textComponent = (
    <Text weight={isSelected ? 'semibold' : 'normal'} size="medium">
      {dateTimeString} {oneOffCoachString}• {instance.duration} min
    </Text>
  );
  const textBadgeComponent = constructInstanceTextBadge(
    instance.isCancelled,
    instanceIsPartOfRecurringSchedule,
    userHasNoRecurringSchedules
  );
  const textRowComponent = userAgent.isDesktop() ? (
    <MediaObject center gutter="small">
      {textComponent}
      {textBadgeComponent}
    </MediaObject>
  ) : (
    <>
      <Row size="small">{textComponent}</Row>
      {textBadgeComponent}
    </>
  );
  const buttonComponent =
    isSelected && sessionTools
      ? constructJoinSessionButton(instance, sessionTools, 'chalkboard', 'xsmall', 'secondary')
      : null;
  const rowComponent = userAgent.isDesktop() ? (
    <MediaObject figure="right" center>
      {textRowComponent}
      {buttonComponent}
    </MediaObject>
  ) : (
    <>
      <Row size="small">{textRowComponent}</Row>
      {buttonComponent}
    </>
  );

  return (
    <div
      className={cx(
        'CoachingSessionUpcomingInstanceRow',
        !isSelected && 'CoachingSessionUpcomingInstanceRow-hoverable'
      )}
      onClick={onSelectInstance}>
      {rowComponent}
    </div>
  );
}

function constructInstanceTextBadge(
  isCancelled: boolean,
  isPartOfRecurringSchedule: boolean,
  userHasNoRecurringSchedules: boolean
) {
  if (isCancelled) {
    // show `Cancelled` if the instance was cancelled
    return (
      <TextBadge color="paper" textSize="xxxxsmall">
        Cancelled
      </TextBadge>
    );
  } else if (isPartOfRecurringSchedule || userHasNoRecurringSchedules) {
    // don't show any badge if the instance is part of a recurring schedule
    // OR if the participant ONLY has one-off instances without a recurring schedule
    return null;
  } else {
    // show `Added` if this is a one-off instance and the user has a recurring schedul
    return (
      <TextBadge color="paper" textSize="xxxxsmall">
        Added
      </TextBadge>
    );
  }
}

/**
 * Format like Thu, Nov 19 at 7:00PM (PDT)
 */
function constructDateTimeString(dateTime: string) {
  return moment(dateTime)
    .tz(moment.tz.guess())
    .format('ddd[,] MMM DD [at] LT [(]z[)]');
}

export function constructJoinSessionButton(
  instance: CoachingSessionInstance,
  sessionTools: CoachingSessionTools,
  buttonColor: ButtonColorUnion,
  buttonSize: ButtonSizeUnion,
  buttonLevel: ButtonLevel
) {
  const minutesFromNow = differenceInMinutes(instance.startDateTime, new Date());
  const isDisabled = minutesFromNow > MAX_MINUTES_BEFORE_SESSION_TO_ALLOW_USER_TO_JOIN;
  const toUrl = `/video/${sessionTools.distanceLearningSessionUuid}`;
  const joinButton = (
    <Button
      eventKey={`${coachingHomeEventPrefix}.join_session.click`}
      data-test="join-session-button"
      disabled={isDisabled}
      href={toUrl}
      level={buttonLevel}
      size={buttonSize}
      color={buttonColor}
      target="_blank">
      Join session
    </Button>
  );

  return isDisabled ? (
    <TooltipTrigger
      placement="bottom"
      tooltip={<Tooltip>You can join 30 minutes before the session</Tooltip>}>
      <div className="CoachingSessionsBySubjectContainer-joinSessionButtonTooltip">
        {joinButton}
      </div>
    </TooltipTrigger>
  ) : (
    joinButton
  );
}
